import React from "react";
import { Plus, Star, Trash2 } from "lucide-react";
import {
  blankPricingTemplate,
  updateTemplate,
  setDefaultTemplate,
  toggleTemplateActive,
  removeTemplate,
} from "utilities/pricingTemplates";

/**
 * SOUND-784 — Kurv pricing templates on the processor record. Internal-only.
 *
 * Controlled: the page owns the list and saves it with the rest of the
 * processor in its one PUT (the API replaces the WHOLE array). Every rule lives
 * in utilities/pricingTemplates.js; this file only renders.
 *
 * Props:
 *   templates   editor rows (normalizePricingTemplates)
 *   onChange    (nextList) => void
 *   rowErrors   { [index]: { templateId?, name?, isDefault? } } — client or server
 *   formError   string | null
 *   disabled
 */
export default function PricingTemplatesEditor({ templates, onChange, rowErrors = {}, formError = null, disabled = false }) {
  return (
    <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Pricing templates</h2>
          <p className="text-sm text-muted-foreground">
            The templates Kurv has issued for this processor. Internal only. The default is preselected when
            transmitting an application; a template that has been used is deactivated, not deleted.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline shrink-0"
          onClick={() => onChange([...templates, blankPricingTemplate()])}
          disabled={disabled}
        >
          <Plus className="h-4 w-4" />
          Add template
        </button>
      </div>

      {formError && (
        <p className="rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive ring-1 ring-destructive/30" role="alert">
          {formError}
        </p>
      )}

      {templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No pricing templates. Applications to this processor cannot be transmitted until one is added.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg ring-1 ring-border">
          {templates.map((t, i) => {
            const err = rowErrors[i] || {};
            return (
              <li key={i} className={`p-3 ${t.active ? "" : "bg-muted/30"}`}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr_auto] md:items-start">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground" htmlFor={`pt-id-${i}`}>
                      Template ID <span className="text-destructive">*</span>
                    </label>
                    <input
                      id={`pt-id-${i}`}
                      type="text"
                      className="input input-bordered w-full py-1.5 text-sm"
                      value={t.templateId}
                      onChange={(e) => onChange(updateTemplate(templates, i, "templateId", e.target.value))}
                      placeholder="e.g. 239"
                      disabled={disabled}
                    />
                    {err.templateId && <p className="mt-1 text-xs text-destructive">{err.templateId}</p>}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground" htmlFor={`pt-name-${i}`}>
                      Name <span className="text-destructive">*</span>
                    </label>
                    <input
                      id={`pt-name-${i}`}
                      type="text"
                      className="input input-bordered w-full py-1.5 text-sm"
                      value={t.name}
                      onChange={(e) => onChange(updateTemplate(templates, i, "name", e.target.value))}
                      placeholder="e.g. Standard e-commerce"
                      disabled={disabled}
                    />
                    {err.name && <p className="mt-1 text-xs text-destructive">{err.name}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:pt-5">
                    {t.isDefault ? (
                      <span className="badge badge-info inline-flex items-center gap-1">
                        <Star className="h-3 w-3" /> Default
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost h-8 px-2 text-xs"
                        onClick={() => onChange(setDefaultTemplate(templates, i))}
                        disabled={disabled}
                        title="Preselect this template when transmitting"
                      >
                        <Star className="h-3.5 w-3.5" /> Set default
                      </button>
                    )}
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground">
                      <input
                        type="checkbox"
                        checked={t.active}
                        onChange={() => onChange(toggleTemplateActive(templates, i))}
                        disabled={disabled}
                        className="h-4 w-4 rounded border-border text-brand"
                      />
                      Active
                    </label>
                    {t.isNew && (
                      <button
                        type="button"
                        className="btn btn-ghost h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => onChange(removeTemplate(templates, i))}
                        disabled={disabled}
                        title="Remove this unsaved template"
                        aria-label="Remove this unsaved template"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                {err.isDefault && <p className="mt-1 text-xs text-destructive">{err.isDefault}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
